import { useState, useEffect, useRef } from "react";
import dashboardOverview from "@/assets/screenshots/dashboard-overview.png";
import emissionsMap from "@/assets/screenshots/emissions-map.png";
import emissionsFlow from "@/assets/screenshots/emissions-flow.png";

// Preload images on module load for instant display
const imageSources = [dashboardOverview, emissionsMap, emissionsFlow];
const preloadedImages: HTMLImageElement[] = [];

imageSources.forEach((src) => {
  const img = new Image();
  img.src = src;
  preloadedImages.push(img);
});

interface ScreenshotCarouselAnimationProps {
  onTabChange?: (index: number) => void;
  showDots?: boolean;
}

const ScreenshotCarouselAnimation = ({ onTabChange, showDots = false }: ScreenshotCarouselAnimationProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const tabs = [
    { id: "overview", label: "Overview", src: dashboardOverview, alt: "Carbonwize dashboard overview - Real-time carbon emissions tracking and analytics" },
    { id: "map", label: "Emissions Map", src: emissionsMap, alt: "Carbonwize emissions map - Geographic visualization of carbon footprint by location" },
    { id: "flow", label: "Emissions Flow", src: emissionsFlow, alt: "Carbonwize emissions flow - Scope 1, 2, 3 carbon data flow visualization" },
  ];

  // Track which images have loaded
  const handleImageLoad = (index: number) => {
    setLoadedImages(prev => new Set([...prev, index]));
  };

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prev) => {
        const next = (prev + 1) % tabs.length;
        onTabChange?.(next);
        return next;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [tabs.length, onTabChange]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    onTabChange?.(index);
  };

  return (
    <div className="relative w-full h-full">
      {/* Screenshot Display - fills entire container */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl">
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            className={`absolute inset-0 transition-all duration-500 ease-out ${
              activeTab === index
                ? "opacity-100 translate-x-0"
                : index < activeTab
                ? "opacity-0 -translate-x-full"
                : "opacity-0 translate-x-full"
            }`}
          >
            {/* Loading skeleton */}
            {!loadedImages.has(index) && (
              <div className="absolute inset-0 bg-dark-foreground/10 animate-pulse rounded-3xl" />
            )}
            <img
              src={tab.src}
              alt={tab.alt}
              loading="eager"
              decoding="async"
              onLoad={() => handleImageLoad(index)}
              className={`w-full h-full object-cover rounded-3xl shadow-2xl transition-opacity duration-300 ${
                loadedImages.has(index) ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </div>
        ))}
      </div>

      {/* Dot Indicators - only shown if showDots is true */}
      {showDots && (
        <div className="absolute bottom-24 left-0 right-0 flex gap-3 justify-center z-10">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(index)}
              aria-label={tab.label}
              className={`h-3 rounded-full transition-all duration-300 ${
                activeTab === index
                  ? "bg-primary w-8"
                  : "bg-white/40 w-3 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { ScreenshotCarouselAnimation };
export default ScreenshotCarouselAnimation;
